import { BlurView } from "expo-blur";
import { Image } from "expo-image";
import { useEffect, useRef, useState } from "react";
import { Animated, StyleSheet, useWindowDimensions } from "react-native";

export function BackgroundImageBlur({
  containerHeight,
  containerWidth,
  displayFlyerUrl,
}: {
  containerHeight?: number;
  containerWidth?: number;
  displayFlyerUrl: null | string | undefined;
}) {
  const { height: windowHeight, width: windowWidth } = useWindowDimensions();
  const w = containerWidth ?? windowWidth;
  const h = containerHeight ?? windowHeight;

  const [currentUrl, setCurrentUrl] = useState<null | string>(null);
  const [nextUrl, setNextUrl] = useState<null | string>(null);
  const currentOpacity = useRef(new Animated.Value(0)).current;
  const nextOpacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!displayFlyerUrl) {
      setCurrentUrl(null);
      setNextUrl(null);
      currentOpacity.setValue(0);
      nextOpacity.setValue(0);
      return;
    }

    if (!currentUrl) {
      setCurrentUrl(displayFlyerUrl);
    } else if (displayFlyerUrl !== currentUrl && displayFlyerUrl !== nextUrl) {
      nextOpacity.setValue(0);
      setNextUrl(displayFlyerUrl);
    }
  }, [currentOpacity, currentUrl, displayFlyerUrl, nextOpacity, nextUrl]);

  const handleCurrentLoaded = () => {
    Animated.timing(currentOpacity, {
      duration: 1000,
      toValue: 1,
      useNativeDriver: false,
    }).start();
  };

  const handleNextLoaded = () => {
    Animated.timing(nextOpacity, {
      duration: 1000,
      toValue: 1,
      useNativeDriver: false,
    }).start(() => {
      setCurrentUrl(nextUrl);
      setNextUrl(null);
      currentOpacity.setValue(1);
      nextOpacity.setValue(0);
    });
  };

  if (!currentUrl) return null;

  return (
    <>
      <BlurLayer
        containerHeight={h}
        containerWidth={w}
        onLoaded={handleCurrentLoaded}
        opacity={currentOpacity}
        url={currentUrl}
      />
      {nextUrl && (
        <BlurLayer
          containerHeight={h}
          containerWidth={w}
          onLoaded={handleNextLoaded}
          opacity={nextOpacity}
          url={nextUrl}
        />
      )}
    </>
  );
}

function BlurLayer({
  containerHeight,
  containerWidth,
  onLoaded,
  opacity,
  url,
}: {
  containerHeight: number;
  containerWidth: number;
  onLoaded?: () => void;
  opacity: Animated.Value;
  url: string;
}) {
  const [naturalSize, setNaturalSize] = useState<{
    height: number;
    width: number;
  } | null>(null);

  const blurIntensity = (() => {
    if (!naturalSize) return 0;
    const coverScale = Math.max(
      containerWidth / naturalSize.width,
      containerHeight / naturalSize.height,
    );
    if (coverScale <= 1) return 0;
    return Math.min(((coverScale - 1) / coverScale) * 100, 100);
  })();

  return (
    <Animated.View style={[StyleSheet.absoluteFill, { opacity }]}>
      <Image
        contentFit="cover"
        onLoad={(e) => {
          const { height: imgH, width: imgW } = e.source;
          setNaturalSize({ height: imgH, width: imgW });
          onLoaded?.();
        }}
        source={{ uri: url }}
        style={styles.backgroundFlyer}
      />
      {blurIntensity > 0 && (
        <BlurView
          intensity={blurIntensity}
          style={StyleSheet.absoluteFill}
          tint="dark"
        />
      )}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  backgroundFlyer: {
    ...StyleSheet.absoluteFillObject,
    height: "100%",
    width: "100%",
  },
});
